import React, { useContext, useState } from 'react';

import config from 'config';
import { useRouter } from 'next/router';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { getMembership, useCustomer } from '@techstyle/react-accounts';
import { FormattedMessage } from '@techstyle/react-intl';
import { useDomain, useSession } from '@techstyle/redux-core';

import { loadAccountPaymentMethods } from '../../actions/account';
import { addMembershipToCart, initCheckout } from '../../actions/checkout';
import { mobile } from '../../styles';
import { useLDFlags } from '../../utils/LD/useLDFlags';
import {
  allowExclusiveCheckout as allowExclusiveCheckoutSelector,
  hasExclusiveProducts as hasExclusiveProductsSelector,
  getMembershipCartItem,
  cartHasOutOfStockItems,
  hasOnlyMembershipCartItem as hasOnlyMembershipCartItemSelector,
  hasMembershipInCart as hasMembershipInCartSelector,
} from '../../utils/selectors';
import { useCheckIfPasswordlessLogin } from '../../utils/useCheckIfPasswordlessLogin';
import { Component as Loading } from '../Loading';
import { MembershipModal } from '../MembershipModal/MembershipModal';
import { Component as PaypalCheckout } from '../PaypalCheckout';
import ToastContext from '../ToastContext/ToastContext';

const paypalActivated = config.get('public.paypal.activated');

const PaypalCheckoutWrapper = styled.div`
  ${mobile`
    ${props =>
      props.theme.context.cartDrawer &&
      `
      padding: 0;
      border-radius: 0;
      margin: 0;
    `}
  `}
`;

export const CartDrawerPaypalButton = () => {
  const { tld } = useDomain();
  const { isVisitor } = useSession();
  const {
    'experiment-checkout-membership-opt-in': checkoutMembershipOptInFlag,
  } = useLDFlags();
  const [isMembershipModalVisible, setIsMembershipModalVisible] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const allowOutOfStockCheckout = useSelector(
    (state: RootStateOrAny) => state.debug?.allowOutOfStockCheckout
  );
  const allowExclusiveCheckout = useSelector(allowExclusiveCheckoutSelector);
  const cart = useSelector((state: RootStateOrAny) => state.checkout);
  const hasExclusiveProducts = useSelector(hasExclusiveProductsSelector);
  const hasOnlyMembershipCartItem = useSelector(
    hasOnlyMembershipCartItemSelector
  );
  const hasMembershipInCart = useSelector(hasMembershipInCartSelector);
  const hasOutOfStockItems = useSelector(cartHasOutOfStockItems);
  const isBorderfreeCustomer = useSelector(
    ({ borderfree }: RootStateOrAny) => borderfree.isBorderfreeCustomer
  );
  const dispatch = useDispatch();
  const isInfluencer = useSelector((state: RootStateOrAny) =>
    Boolean(state.customer.isInfluencer)
  );
  const { showMessage: showToastMessage } = useContext(
    // eslint-disable-next-line import/no-named-as-default-member
    ToastContext.Context as React.Context<{
      toastMessage: any;
      showMessage: (msg) => void;
      hideMessage: () => void;
    }>
  );

  const membershipItem = useSelector(getMembershipCartItem);
  const isVip = useSelector(
    (state: RootStateOrAny) => getMembership(state).isVip
  );
  const profile = useCustomer();
  const router = useRouter();

  const { isLoading: isPasswordlessCheckLoading, isPasswordlessLogin } =
    useCheckIfPasswordlessLogin();
  const isPasswordless = isPasswordlessCheckLoading
    ? true
    : Boolean(isPasswordlessLogin);

  const hasMembershipItem = !!membershipItem;
  const showExclusiveProductMessage =
    hasExclusiveProducts && !isVip && !hasMembershipItem;
  const showPaypalExpress =
    paypalActivated[tld] &&
    !isBorderfreeCustomer &&
    !isInfluencer &&
    !isVisitor &&
    !isPasswordless;

  const disableButton =
    hasOnlyMembershipCartItem ||
    (showExclusiveProductMessage && !allowExclusiveCheckout) ||
    (hasOutOfStockItems && !allowOutOfStockCheckout) ||
    (isBorderfreeCustomer && membershipItem);
  const isMembershipModalEnabled = checkoutMembershipOptInFlag === 'modal';

  const stopLoadingAndError = () => {
    setLoading(false);
    showToastMessage(
      <FormattedMessage
        id="global_checkout.general_error"
        defaultMessage="Oops we’ve encountered an error. Try again"
      />
    );
  };

  const checkout = async () => {
    if (!isBorderfreeCustomer) {
      setLoading(true);
      try {
        dispatch(initCheckout());
      } catch (err) {
        stopLoadingAndError();
      }
    }
  };

  const handleContinue = async () => {
    if (isVip || hasMembershipInCart || !isMembershipModalEnabled) {
      checkout();
    } else {
      setIsMembershipModalVisible(true);
    }
  };

  const onSuccessPaypal = async () => {
    await dispatch(loadAccountPaymentMethods());
    handleContinue();
  };

  const onCloseMembershipModal = () => {
    setIsMembershipModalVisible(false);
  };

  const onContinueAsMember = async () => {
    setLoading(true);
    await dispatch(addMembershipToCart());
    await checkout();
    setLoading(false);
  };

  const onContinueAsGuest = () => {
    if (profile.id) {
      checkout();
    } else {
      router.push('/checkout/sign-up');
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (isMembershipModalVisible) {
    return (
      <MembershipModal
        guestPrice={cart.nonVipSubtotal}
        memberPrice={cart.vipSubtotal}
        onContinueAsMember={onContinueAsMember}
        onContinueAsGuest={onContinueAsGuest}
        onCancel={onCloseMembershipModal}
        isLoggedIn={!!profile.id}
        membershipPrice={undefined}
      />
    );
  }

  return !disableButton && showPaypalExpress ? (
    <PaypalCheckoutWrapper
      data-autotag="paypal-button"
      data-qa-automation="PaypalButton"
    >
      <PaypalCheckout
        fromCart
        onSuccess={onSuccessPaypal}
        updateLoading={setLoading}
      />
    </PaypalCheckoutWrapper>
  ) : null;
};
